import { useEffect, useRef, useState } from 'react';

/**
 * Contador animado con ease-out — para stats de conversión.
 * Se dispara una vez cuando el elemento entra en viewport.
 */
export function useCountUp(target: number, duration = 1600, decimals = 0) {
  const [value, setValue] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const run = () => {
      if (started.current) return;
      started.current = true;
      if (reduced) {
        setValue(target);
        return;
      }
      const start = performance.now();
      const ease = (t: number) => 1 - Math.pow(1 - t, 4); // quart ease-out
      const tick = (now: number) => {
        const p = Math.min((now - start) / duration, 1);
        setValue(parseFloat((target * ease(p)).toFixed(decimals)));
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          run();
          io.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [target, duration, decimals]);

  return { ref, value };
}
